import UserDetail from "components/ui/UserDetail/UserDetail";
import BackBar from "components/ui/BackBar/BackBar";
import Label from "components/ui/Labels/Label";
import ContentContainer from "layout/Content/ContentContainer";
import useEntity from "hooks/useEntity";
import Alert from "components/ui/Alert/Alert";
import ResponsiveImage from "components/ui/ResponsiveImage/ResponsiveImage";
import Loader from "components/ui/Loader/Loader";
import HTMLContainer from "components/ui/HTML/HTMLContainer";
import Tabs from "components/ui/Tabs/Tabs";
import GroupMemberAccessButton from "features/groups/components/GroupMemberAccessButton";
import GroupMembers from "features/groups/components/GroupMembers";
import { useEffect, useState } from "react";
import GroupMemberLeaveButton from "features/groups/components/GroupMemberLeaveButton";
import GroupBulletins from "features/groups/components/GroupBulletins";
import Trans from "features/translation/components/Trans";
import { ReactComponent as GroupIcon } from "../../assets/images/icons/groups.svg";
import "features/groups/assets/styles/Group.scss";
import BulletinCreateButton from "features/bulletins/components/BulletinCreateButton";
import { useGroupAccess } from "features/groups/hooks/useGroupAccess";

function Group() {
  const [refreshToken, setRefreshToken] = useState();
  const [memberCount, setMemberCount] = useState();
  const { entity, isLoading } = useEntity(window.location.pathname, 'group', refreshToken);
  const { working, checkAccess } = useGroupAccess();
  const [groupAccess, setGroupAccess] = useState();

  useEffect(
    () => {
      const doCheckAccess = async () => {
        const data = await checkAccess(entity.id);
        setGroupAccess(data);
      }

      if (entity?.id) {
        doCheckAccess();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading]
  );

  if (isLoading) {
    return <Loader containerVariant={ 'fixed' }/>;
  }

  if (!entity) {
    return <Alert/>;
  }

  if (!memberCount && memberCount !== 0 && entity.field_member_count?.value) {
    setMemberCount(parseInt(entity.field_member_count.value));
  }

  const refresh = () => {
    setRefreshToken(Math.random());
  }

  const onMemberCountUpdated = (count) => {
    setMemberCount(count)
  }

  const isPrivate = entity.field_group_is_private.value === '1';
  const groupMemberId = groupAccess?.id;
  const isMember = groupAccess?.is_member;
  const isAdmin = groupAccess?.is_admin;
  const isRemoved = groupAccess?.status === 'removed';
  const isWaitingForApproval = groupAccess?.status === 'access_requested';


  let infoMessage = null;
  if (isWaitingForApproval) {
    infoMessage = `U heeft toegang aangevraagd tot deze groep, uw aanvraag zal nu door de beheerder worden beoordeeld.`;
  }

  let warningMessage = null;
  if (isRemoved) {
    warningMessage = `U bent door de beheerder uit deze groep verwijderd, u heeft geen toegang meer tot de groep. U heeft een e-mail ontvangen met meer informatie, lees deze goed voordat u eventueel opnieuw toegang aanvraagt.`;
  }

  return (
    <>
      <BackBar containerWidth={ 'small' } label={ 'Terug naar groepen' }/>

      <ContentContainer contentGridType={ 'small' } contentPadding={ true } contentBackground={ true }>

        <div className="group-nav">
          <div className="group-nav__labels">
            <Label variant={ 'dark' }>
              { isPrivate ? 'Besloten groep' : 'Openbaar' }
            </Label>

            <Label>
              <GroupIcon/>
              { memberCount === 1 ? '1 deelnemer' : `${ memberCount } deelnemers` }
            </Label>
          </div>
          <div className="group-nav__action">
            {
              isMember && !working &&
              <GroupMemberLeaveButton groupMemberId={ groupMemberId }
                                      isPrivate={ isPrivate }
                                      isAdmin={ isAdmin }
                                      onCompleted={ () => {
                                        refresh();
                                      }
                                      }/>
            }

            {
              !isMember && !working && !isWaitingForApproval &&
              <GroupMemberAccessButton groupId={ entity.id }
                                       groupMemberId={ groupMemberId }
                                       isPrivate={ isPrivate }
                                       onCompleted={ () => {
                                         refresh();
                                       }
                                       }/>
            }
          </div>
        </div>

        {
          warningMessage &&
          <Alert
            title={ warningMessage }
            type={ `warning` }/>
        }

        {
          infoMessage &&
          <Alert
            title={ infoMessage }
            type={ `success` }/>
        }

        <div className={ 'wrapper--margin-small' }>
          <h2>{ entity.title.value }</h2>

          <UserDetail
            image={ entity.profile?.field_profile_photo?.value }
            username={ entity.profile?.title?.value }
            subtext={ `${ entity.profile?.field_profile_city?.value || `Onbekende gebruiker` } (Beheerder)` }
            marginBottom={ true }
            url={ entity.profile?.url }
          />
        </div>

        <div className={ 'wrapper--margin-medium' }>

          <div className="wrapper--margin-small">
            { isMember && !working &&
              <BulletinCreateButton
                groupId={ parseInt(entity.id) }
                categoryId={ entity.bulletin_category_id.value }/>
            }
          </div>

          <Tabs>
            <div label="Omschrijving">
              <HTMLContainer>
                { entity.field_description.display }
              </HTMLContainer>

              {
                entity.field_group_photo.value &&
                <div className={ 'wrapper--margin-xsmall' }>
                  <ResponsiveImage value={ entity.field_group_photo.value }/>
                </div>
              }
            </div>


            <div label="Groepsleden">
              {
                isMember || !isPrivate || isAdmin ?
                  <GroupMembers groupId={ entity.id }
                                groupIsPrivate={ isPrivate }
                                currentUserIsGroupAdmin={ isAdmin }
                                onMemberCountUpdated={ onMemberCountUpdated }

                  />
                  :
                  <p><Trans text={ `Dit is een besloten groep. Groepsleden zijn alleen zichtbaar voor leden.` }/></p>
              }
            </div>

            <div label="Groepsberichten">
              { isMember || !isPrivate || isAdmin ?
                <GroupBulletins groupId={ parseInt(entity.id) }
                                groupIsPrivate={ isPrivate }/>
                :
                <p><Trans text={ `Dit is een besloten groep. Groepsberichten zijn alleen zichtbaar voor leden.` }/></p>
              }
            </div>

          </Tabs>
        </div>

      </ContentContainer>
    </>
  );
}

export default Group;
